import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Accordion,
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import {
  FilterCategory,
  FilterItem,
  mockFilterCategories,
} from "../../data/mockFilters";

export function OperationsFilters() {
  const [filterCategories, setFilterCategories] =
    useState<FilterCategory[]>(mockFilterCategories);
  const [searchQuery, setSearchQuery] = useState("");
  const [opened, { open, close }] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [itemModalOpened, { open: openItemModal, close: closeItemModal }] =
    useDisclosure(false);

  const [currentCategory, setCurrentCategory] = useState<FilterCategory | null>(
    null
  );
  const [currentItem, setCurrentItem] = useState<FilterItem | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    id: "",
    name: "",
    description: "",
  });

  const [itemForm, setItemForm] = useState({
    id: "",
    name: "",
    description: "",
    active: true,
  });

  // Filter categories based on search query
  const filteredCategories = filterCategories.filter((category) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(query) ||
      category.description?.toLowerCase().includes(query) ||
      category.items.some(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
      )
    );
  });

  // Handlers for category operations
  const handleAddCategory = () => {
    setIsEditing(false);
    setCategoryForm({
      id: "",
      name: "",
      description: "",
    });
    open();
  };

  const handleEditCategory = (category: FilterCategory) => {
    setIsEditing(true);
    setCurrentCategory(category);
    setCategoryForm({
      id: category.id,
      name: category.name,
      description: category.description || "",
    });
    open();
  };

  const handleDeleteCategory = (category: FilterCategory) => {
    setCurrentCategory(category);
    openDeleteModal();
  };

  const confirmDeleteCategory = () => {
    if (currentCategory) {
      setFilterCategories((prev) =>
        prev.filter((cat) => cat.id !== currentCategory.id)
      );
      closeDeleteModal();
    }
  };

  const handleSaveCategory = () => {
    const { name, description } = categoryForm;

    if (!name.trim()) return;

    if (isEditing && currentCategory) {
      // Update existing category
      setFilterCategories((prev) =>
        prev.map((cat) =>
          cat.id === currentCategory.id
            ? { ...cat, name, description: description || undefined }
            : cat
        )
      );
    } else {
      // Create new category
      const newId = `cat-${Date.now()}`;
      setFilterCategories((prev) => [
        ...prev,
        {
          id: newId,
          name,
          description: description || undefined,
          items: [],
        },
      ]);
    }

    close();
  };

  // Handlers for filter item operations
  const handleAddItem = (category: FilterCategory) => {
    setCurrentCategory(category);
    setIsEditingItem(false);
    setItemForm({
      id: "",
      name: "",
      description: "",
      active: true,
    });
    openItemModal();
  };

  const handleEditItem = (category: FilterCategory, item: FilterItem) => {
    setCurrentCategory(category);
    setCurrentItem(item);
    setIsEditingItem(true);
    setItemForm({
      id: item.id,
      name: item.name,
      description: item.description || "",
      active: item.active,
    });
    openItemModal();
  };

  const handleDeleteItem = (categoryId: string, itemId: string) => {
    setFilterCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? { ...cat, items: cat.items.filter((item) => item.id !== itemId) }
          : cat
      )
    );
  };

  const handleSaveItem = () => {
    if (!currentCategory || !itemForm.name.trim()) return;

    const { name, description, active } = itemForm;

    if (isEditingItem && currentItem) {
      // Update existing item
      setFilterCategories((prev) =>
        prev.map((cat) =>
          cat.id === currentCategory.id
            ? {
                ...cat,
                items: cat.items.map((item) =>
                  item.id === currentItem.id
                    ? {
                        ...item,
                        name,
                        description: description || undefined,
                        active,
                      }
                    : item
                ),
              }
            : cat
        )
      );
    } else {
      // Create new item
      const newId = `item-${Date.now()}`;
      setFilterCategories((prev) =>
        prev.map((cat) =>
          cat.id === currentCategory.id
            ? {
                ...cat,
                items: [
                  ...cat.items,
                  {
                    id: newId,
                    name,
                    description: description || undefined,
                    active,
                  },
                ],
              }
            : cat
        )
      );
    }

    closeItemModal();
  };

  // Toggle item active status
  const toggleItemActive = (categoryId: string, itemId: string) => {
    setFilterCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              items: cat.items.map((item) =>
                item.id === itemId ? { ...item, active: !item.active } : item
              ),
            }
          : cat
      )
    );
  };

  return (
    <Container size="xl" py="xl">
      <Breadcrumb
        items={[
          { label: "Operations Panel", href: "/operations" },
          { label: "Filters", isActive: true },
        ]}
      />
      <Group justify="space-between" mb="xl">
        <Stack gap={0}>
          <Title order={1} size="h2" fw={700}>
            Filters Management
          </Title>
          <Text c="dimmed" size="lg">
            Create and manage filters for categorizing CVs
          </Text>
        </Stack>
        <Button
          leftSection={<PlusIcon style={{ width: rem(18) }} />}
          onClick={handleAddCategory}
        >
          Add Filter Category
        </Button>
      </Group>

      {/* Search bar */}
      <Box mb="lg">
        <TextInput
          placeholder="Search filter categories and items..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          leftSection={
            <MagnifyingGlassIcon style={{ width: rem(18), height: rem(18) }} />
          }
          rightSection={
            searchQuery ? (
              <ActionIcon
                onClick={() => setSearchQuery("")}
                variant="subtle"
                size="sm"
              >
                <XMarkIcon style={{ width: rem(16), height: rem(16) }} />
              </ActionIcon>
            ) : null
          }
          size="md"
        />
      </Box>

      {filteredCategories.length === 0 ? (
        <Alert
          icon={<ExclamationTriangleIcon style={{ width: rem(24) }} />}
          title="No filters found"
          color="gray"
        >
          {searchQuery
            ? "No filter categories match your search. Try a different query or create a new filter category."
            : "No filter categories have been created yet. Click 'Add Filter Category' to get started."}
        </Alert>
      ) : (
        <Accordion variant="separated">
          {filteredCategories.map((category) => (
            <Accordion.Item value={category.id} key={category.id}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text fw={600}>{category.name}</Text>
                    {category.description && (
                      <Text size="sm" c="dimmed">
                        {category.description}
                      </Text>
                    )}
                  </div>
                  <Text size="sm" c="dimmed">
                    {category.items.length} item
                    {category.items.length !== 1 ? "s" : ""}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Group justify="space-between">
                    <Group>
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<PencilIcon style={{ width: rem(14) }} />}
                        onClick={() => handleEditCategory(category)}
                      >
                        Edit Category
                      </Button>
                      <Button
                        variant="light"
                        color="red"
                        size="xs"
                        leftSection={<TrashIcon style={{ width: rem(14) }} />}
                        onClick={() => handleDeleteCategory(category)}
                      >
                        Delete
                      </Button>
                    </Group>
                    <Button
                      size="xs"
                      leftSection={<PlusIcon style={{ width: rem(14) }} />}
                      onClick={() => handleAddItem(category)}
                    >
                      Add Filter Item
                    </Button>
                  </Group>

                  <Divider />

                  {category.items.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      No filter items yet. Add some using the button above.
                    </Text>
                  ) : (
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                      {category.items.map((item) => (
                        <Card
                          key={item.id}
                          withBorder
                          p="md"
                          radius="md"
                          style={{
                            opacity: item.active ? 1 : 0.7,
                            borderColor: item.active
                              ? undefined
                              : "var(--mantine-color-gray-4)",
                          }}
                        >
                          <Group justify="space-between" mb="xs">
                            <Switch
                              checked={item.active}
                              onChange={() =>
                                toggleItemActive(category.id, item.id)
                              }
                              labelPosition="left"
                              label="Active"
                              size="xs"
                            />
                            <Group gap="xs">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEditItem(category, item)}
                              >
                                <PencilIcon style={{ width: rem(16) }} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() =>
                                  handleDeleteItem(category.id, item.id)
                                }
                              >
                                <TrashIcon style={{ width: rem(16) }} />
                              </ActionIcon>
                            </Group>
                          </Group>

                          <Text fw={600}>{item.name}</Text>
                          {item.description && (
                            <Text size="sm" c="dimmed" mt="xs">
                              {item.description}
                            </Text>
                          )}
                        </Card>
                      ))}
                    </SimpleGrid>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* Category Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={isEditing ? "Edit Filter Category" : "Add Filter Category"}
        centered
      >
        <Stack>
          <TextInput
            label="Category Name"
            placeholder="e.g., Department, Skill Level, etc."
            value={categoryForm.name}
            onChange={(e) =>
              setCategoryForm({ ...categoryForm, name: e.target.value })
            }
            required
          />
          <Textarea
            label="Description (optional)"
            placeholder="Description of this filter category"
            value={categoryForm.description}
            onChange={(e) =>
              setCategoryForm({ ...categoryForm, description: e.target.value })
            }
            autosize
            minRows={2}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Item Modal */}
      <Modal
        opened={itemModalOpened}
        onClose={closeItemModal}
        title={isEditingItem ? "Edit Filter Item" : "Add Filter Item"}
        centered
      >
        <Stack>
          <TextInput
            label="Item Name"
            placeholder="e.g., Junior, Senior, Marketing, etc."
            value={itemForm.name}
            onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
            required
          />
          <Textarea
            label="Description (optional)"
            placeholder="Description of this filter item"
            value={itemForm.description}
            onChange={(e) =>
              setItemForm({ ...itemForm, description: e.target.value })
            }
            autosize
            minRows={2}
          />
          <Switch
            label="Active"
            checked={itemForm.active}
            onChange={(e) =>
              setItemForm({ ...itemForm, active: e.target.checked })
            }
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeItemModal}>
              Cancel
            </Button>
            <Button onClick={handleSaveItem}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        title="Confirm Deletion"
      >
        <Stack>
          <Text>
            Are you sure you want to delete the filter category "
            {currentCategory?.name}"? This will also delete all items within
            this category.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDeleteCategory}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
